import { useCallback, useMemo } from "react";
// Components
import { FilterSearchTable } from "src/components/FilterSearchTable/FilterSearchTable";
// Redux (to focus selected in the timeline)
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Button, Grid, useTheme, Theme, Box } from "@mui/material";
import { IContent } from "@vmosolutions/oc-interfaces";
import { useDispatch } from "react-redux";
import { ITableColumn } from "src/components/FilterSearchTable/TableComponents/TableHeader";
import { openWindow } from "src/redux/actions/Mosaic.Actions";
// Permissions imports
import { hasPermission, PolicyAction, PolicyResource } from "@vmosolutions/oc-interfaces";
import { useSelector } from "react-redux";
import { IRootState } from "src/redux/reducers/CombinedReducers";

interface IContentListWindowProps {
  path?: string[];
  id?: string;
  windowProps?: object;
  data?: IContent[];
}

/////////////////////////////////////////////////////
/////             STATIC TABLE DATA            //////
/////////////////////////////////////////////////////

// ASSIGNED FLIGHTS
const assignedFlightsTableDesc = {
  tableTitle: "Content",
  tableDescription: "",
  noMessage: "No content to display, use the filter above to search.",
};
const assignedFlightsTableInfo: ITableColumn[] = [
  {
    id: "manageContent",
    value: "manageContent",
    index: 0,
    numeric: true,
    label: "Manage Content",
    type: "string",
    width: 1,
    visible: true,
    filter: false,
  },
  {
    id: "thumbnail",
    value: "thumbnail",
    index: 1,
    numeric: true,
    label: "Thumbnail",
    type: "image",
    width: 10,
    visible: true,
    filter: false,
  },
  {
    id: "contentType",
    value: "contentType",
    index: 2,
    numeric: false,
    label: "Content Type",
    type: "string",
    width: 10,
    visible: true,
    filter: true,
  },
  {
    id: "fileName",
    value: "fileName",
    index: 3,
    numeric: false,
    label: "File Name",
    type: "string",
    width: 10,
    visible: true,
    filter: true,
  },
  {
    id: "fileType",
    value: "fileType",
    index: 4,
    numeric: false,
    label: "File Type",
    type: "string",
    width: 10,
    visible: true,
    filter: true,
  },
  {
    id: "uploadedBy",
    value: "uploadedBy",
    index: 5,
    numeric: false,
    label: "Uploaded By",
    type: "string",
    width: 10,
    visible: false,
    filter: true,
  },
  {
    id: "createdAt",
    value: "createdAt",
    index: 6,
    numeric: false,
    label: "Created At (UTC)",
    type: "date",
    width: 10,
    visible: true,
    filter: true,
  },
  {
    id: "modifiedAt",
    value: "modifiedAt",
    index: 7,
    numeric: false,
    label: "Modified At (UTC)",
    type: "date",
    width: 10,
    visible: false,
    filter: true,
  },
  {
    id: "associatedFlights",
    value: "associateFlights.length",
    index: 8,
    numeric: true,
    label: "Associations",
    type: "number",
    width: 10,
    visible: true,
    filter: true,
  },
  {
    id: "isValid",
    value: "isValid",
    index: 9,
    numeric: true,
    label: "Validation Status",
    type: "boolean",
    width: 10,
    visible: true,
    filter: true,
  },
  {
    id: "source",
    value: "source",
    index: 10,
    numeric: true,
    type: "icon",
    label: "Source",
    width: 10,
    visible: true,
    filter: true,
  },
];

const ContentTableStyles = (theme: Theme): any => ({
  padding: "15px",
  width: "100%",
  "& .create_content_btn": {
    fontSize: 12,
    padding: "2px 8px",
    "& svg": {
      width: 12,
      height: 12,
      marginRight: "5px",
    },
  },
});

/////////////////////////////////////////////////////
///////////  ADVANCED SEARCH FLIGHTS
/////////////////////////////////////////////////////
export const ContentListWindow = (props: IContentListWindowProps): JSX.Element => {
  const { id, path } = props;
  const dispatch = useDispatch();

  // Permissions
  const policies = useSelector((state: IRootState) => state.authentication?.attributes?.policies);
  const canCreateContent = hasPermission(policies, PolicyResource.Content, PolicyAction.List);

  // Function used to create the dataSet needed for the table component.
  // This particular dataSet contains an array with all the available data types for the advanced search functionality.
  // use useMemo to prevent the whole table from re-rendering
  const tableData: any = useMemo(
    () => [
      {
        id: "content",
        dataType: "content",
        label: "Content",
        data: [] as any[],
        tableInfo: assignedFlightsTableInfo,
        tableInfoDisplay: assignedFlightsTableDesc,
      },
    ],
    []
  );

  const openManageContentWindow = useCallback((): void => {
    //The props change in this window which causes the whole table to render
    //use useMemo/useCallback to prevent that
    dispatch(openWindow("Create Content", "ManageContentWindow", [], {}, "context"));
  }, [dispatch]);

  const theme = useTheme();
  const mosaicPanel = useMemo(() => `${id}-panel`, [id]);

  return (
    <Box sx={ContentTableStyles(theme)}>
      <FilterSearchTable
        id={id}
        path={path}
        className="content_management"
        events={tableData}
        parentId={mosaicPanel}
        uniqueKey={"_id"}
        allowMultiFilter={true}
        hideFocusSelected={true}
        //changeSelectedRows={changeSelectedRows}
        selectModeSingle={true}
        hideSelectionCheckboxes={true}
        selectUsingCheckboxesOnly={true}
        showEventActions={true}
        showSavedFilters={true}
        optionalControls={
          <Grid container style={{ width: "auto" }}>
            <Button
              id={"create_content_btn"}
              variant="outlined"
              color="primary"
              disabled={!canCreateContent}
              onClick={openManageContentWindow}
              className="create_content_btn"
            >
              <OpenInNewIcon />
              CREATE NEW CONTENT
            </Button>
          </Grid>
        }
      />
    </Box>
  );
};
